/**
 * `citationDisplayNames.ts` — relabel a document citation with the name an
 * author set in the Define app, on the way to the user.
 *
 * `AIFileCitation.fileName` arrives from OpenAI's `file_search` annotation and
 * is the UPLOADED FILE's name (`Robodog_Manual 1.pdf`). Inside the knowledge
 * transport that is IDENTITY — `findPdfAsync` matches a requested PDF against
 * it and `withSources` de-duplicates on it — so the rename happens here, at
 * the edge, after every match is settled.
 *
 * ⚠ Only `fileName` is rewritten. `fileId` and `storagePath` are what the
 * chip's click handler opens and what the source list de-duplicates on, so
 * they are carried through untouched; a display name is not unique and two
 * documents may legitimately share one.
 *
 * ⚠ THE CHIPS ONLY (`publishCitations`). The `Sources (n): …` line appended to
 * the text the AGENT reads keeps the RAW file name, and so does the opened-PDF
 * label - user direction 2026-09-04, "I only wanted the presentation in the doc
 * reference to be the name of the document". That line is the model's own
 * input, and a title is frequently LESS specific than its filename, which takes
 * away the word the agent uses to notice it is holding the wrong document.
 * Do not "restore the consistency" by wiring `withSources` through here.
 *
 * ⚠ Known cost of that split, recorded rather than hidden: the Answer skill
 * routes those same raw names into `present_options` buttons, so a user
 * choosing between documents can see file names on the buttons and titles on
 * the chips. If that is reported, the fix is the BUTTON labels, not this line.
 */

import type { AIFileCitation } from "@ai/context/AIContextEngineModel.KnowledgeTransport";
import { resolveDocumentName } from "@models/docs/documentDisplayNames";
import type { AgentToolExecutorHost } from "./AgentToolExecutorModel";

export async function withDocumentDisplayNames(
  host: AgentToolExecutorHost,
  citations: ReadonlyArray<AIFileCitation> | null,
): Promise<ReadonlyArray<AIFileCitation> | null> {
  if (!citations?.length) return citations;
  if (!host.loadDocumentNameIndexAsync) {
    // A host built before this seam existed - in dev, a live session whose
    // orchestrator predates a hot reload. Distinguishable from "never ran".
    if (import.meta.env.DEV) console.warn("[citationDisplayNames] host has no loadDocumentNameIndexAsync");
    return citations;
  }

  const index = await host.loadDocumentNameIndexAsync();
  if (index.size === 0) {
    if (import.meta.env.DEV) console.warn("[citationDisplayNames] document name index is EMPTY");
    return citations;
  }

  return citations.map((citation) => {
    const display = resolveDocumentName(index, citation.storagePath, citation.fileName);
    if (import.meta.env.DEV && !display) {
      // Why a chip still shows its file name. The three causes look identical
      // on screen: the library never loaded, the keys do not line up, or the
      // Define row carries no `documentName`, so print both sides.
      console.warn(
        "[citationDisplayNames] unresolved",
        { storagePath: citation.storagePath, fileName: citation.fileName },
        "index:", [...index.entries()].slice(0, 20),
      );
    }
    return display && display !== citation.fileName
      ? { ...citation, fileName: display }
      : citation;
  });
}
